import {useMemo, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {useTranslation} from 'react-i18next'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import {Button} from '@/components/ui/button'
import {Card, CardContent} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {Input} from '@/components/ui/input'
import {ArrowLeft, ArrowRight, Calendar, Clock, Search} from 'lucide-react'
import {Link} from 'react-router-dom'
import {useBlogPosts} from '@/hooks/useBlogPosts'
import {urlForImage} from '@/lib/sanity'
import {blogCategoryLabel} from '@/lib/blogCategories'
import {formatMediumDate} from '@/lib/dates'
import type {BlogCategory, BlogPostSummary} from '@/types/blog'

const ALL_CATEGORIES: BlogCategory[] = ['Learn', 'Teach', 'Live', 'Insights', 'Inside Jynio']

const coverUrl = (post: BlogPostSummary, width: number) =>
  post.coverImage?.asset
    ? urlForImage(post.coverImage).width(width).fit('max').auto('format').url()
    : null

const ArticleCard = ({article}: {article: BlogPostSummary}) => {
  const {t, i18n} = useTranslation()
  const src = coverUrl(article, 800)
  return (
    <Link to={`/blog/${article.slug}`} className="group block h-full">
      <Card className="h-full overflow-hidden hover:border-foreground/20 transition-colors">
        <div className="aspect-[16/9] bg-secondary overflow-hidden">
          {src ? (
            <img
              src={src}
              alt={article.coverImage?.alt ?? article.title}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : null}
        </div>
        <CardContent className="p-5 flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            {article.categories.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {blogCategoryLabel(t, tag)}
              </Badge>
            ))}
          </div>
          <h3 className="font-display font-semibold text-foreground text-lg leading-snug group-hover:text-foreground/80 transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-3">{article.excerpt}</p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-auto pt-2">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {formatMediumDate(article.publishedAt, i18n.language)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> {t('blog.minRead', {count: article.readingMinutes})}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

const FeaturedCard = ({article}: {article: BlogPostSummary}) => {
  const {t, i18n} = useTranslation()
  const src = coverUrl(article, 1200)
  return (
    <Link to={`/blog/${article.slug}`} className="group block">
      <Card className="overflow-hidden hover:border-foreground/20 transition-colors">
        <div className="grid md:grid-cols-2">
          <div className="aspect-[16/9] md:aspect-auto bg-secondary overflow-hidden">
            {src ? (
              <img
                src={src}
                alt={article.coverImage?.alt ?? article.title}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            ) : null}
          </div>
          <CardContent className="p-8 md:p-10 flex flex-col justify-center gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="default" className="text-xs">
                {t('blog.featuredBadge')}
              </Badge>
              {article.categories.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {blogCategoryLabel(t, tag)}
                </Badge>
              ))}
            </div>
            <h2 className="font-display font-bold text-foreground text-2xl md:text-3xl leading-tight">
              {article.title}
            </h2>
            <p className="text-muted-foreground">{article.excerpt}</p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {formatMediumDate(article.publishedAt, i18n.language)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {t('blog.minRead', {count: article.readingMinutes})}
              </span>
              <span>{t('blog.byAuthor', {name: article.author.name})}</span>
            </div>
            <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground group-hover:gap-2 transition-all">
              {t('blog.readArticle')} <ArrowRight className="w-4 h-4" />
            </span>
          </CardContent>
        </div>
      </Card>
    </Link>
  )
}

const Blog = () => {
  const {t} = useTranslation()
  const [search, setSearch] = useState('')
  const [activeTag, setActiveTag] = useState<BlogCategory | null>(null)

  const {data: articles, isLoading, isError} = useBlogPosts()

  const featured = useMemo(() => articles?.find((a) => a.featured), [articles])
  const isFiltering = search.trim() !== '' || activeTag !== null

  const filtered = useMemo(() => {
    if (!articles) return []
    const q = search.trim().toLowerCase()
    return articles.filter((a) => {
      const matchesSearch =
        q === '' ||
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q)
      const matchesTag = !activeTag || a.categories.includes(activeTag)
      return matchesSearch && matchesTag
    })
  }, [articles, search, activeTag])

  const gridArticles = isFiltering
    ? filtered
    : filtered.filter((a) => a.slug !== featured?.slug)

  return (
    <div className="min-h-screen bg-background">
      {/* Title only: the Rails layout owns description/og/twitter (#674). */}
      <Helmet>
        <title>{t('blog.metaTitle')}</title>
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-24 max-w-6xl">
        <Button variant="ghost" size="sm" className="mb-6 -ml-2" asChild>
          <Link to="/">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t('nav.back')}
          </Link>
        </Button>

        <div className="max-w-2xl mb-12">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-3">
            {t('blog.title')}
          </h1>
          <p className="text-lg text-muted-foreground">{t('blog.subtitle')}</p>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
          <div className="relative md:max-w-sm md:flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('blog.searchPlaceholder')}
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge
              variant={activeTag === null ? 'default' : 'secondary'}
              className="cursor-pointer"
              onClick={() => setActiveTag(null)}
            >
              {t('blog.filterAll')}
            </Badge>
            {ALL_CATEGORIES.map((tag) => (
              <Badge
                key={tag}
                variant={activeTag === tag ? 'default' : 'secondary'}
                className="cursor-pointer"
                onClick={() => setActiveTag(tag)}
              >
                {blogCategoryLabel(t, tag)}
              </Badge>
            ))}
          </div>
        </div>

        {!isFiltering && featured && (
          <div className="mb-12">
            <FeaturedCard article={featured} />
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-16 text-muted-foreground">{t('blog.loadingArticles')}</div>
        ) : isError ? (
          <div className="text-center py-16 text-muted-foreground">
            <p>{t('blog.loadArticlesError')}</p>
          </div>
        ) : gridArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gridArticles.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-muted-foreground">
            <p>{t('blog.noArticlesFound')}</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  )
}

export default Blog
